import { Alert, MenuItem, Stack, TextField, Typography } from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import type { BiomeshSensorResponse } from "@phc/common/gen/threatAnalyticsGen"
import {
  getListSensorsBiomeshSensorListGetQueryKey,
  useCreateSensorBiomeshSensorPost,
  useListContactsBiomeshContactListGet,
  useListPlatformsBiomeshPlatformListGet,
  useUpdateSensorBiomeshSensorSensorIdPut,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import type { Dayjs } from "dayjs"
import { enqueueSnackbar } from "notistack"
import type { FC } from "react"
import { useCallback, useState } from "react"
import { dayjsUTC } from "../../utils/dates"
import { Modal } from "../Shared/Modal"
import { ContactFormDialog } from "./ContactFormDialog"
import {
  CREATE_NEW_SENTINEL,
  extractApiError,
  isOperationalStatus,
  isSensorFunction,
  isSensorType,
  operationalStatusOptions,
  sensorFunctionOptions,
  sensorTypeOptions,
} from "./copUtils"
import { MultiAutocomplete } from "./MultiAutocomplete"
import { NodeFormDialog } from "./NodeFormDialog"

interface SensorFormDialogProps {
  open: boolean
  onClose: () => void
  sensor?: BiomeshSensorResponse
  onSaved?: (sensorId: string) => void
  /** Pre-select node when creating a sensor from a node context */
  initialPlatformId?: string
}

export const SensorFormDialog: FC<SensorFormDialogProps> = ({
  open,
  onClose,
  sensor,
  onSaved,
  initialPlatformId,
}) => {
  const isEdit = !!sensor
  const queryClient = useQueryClient()

  const [sensorName, setSensorName] = useState(sensor?.sensor_name ?? "")
  const [operationalStatus, setOperationalStatus] = useState(
    sensor?.operational_status ?? ""
  )
  const [sensorType, setSensorType] = useState(sensor?.sensor_type ?? "")
  const [sensorFunction, setSensorFunction] = useState(
    sensor?.sensor_function ?? ""
  )
  const [sensorMakeModel, setSensorMakeModel] = useState(
    sensor?.sensor_make_model ?? ""
  )
  const [lastSampleCollected, setLastSampleCollected] = useState<Dayjs | null>(
    sensor?.last_sample_collected_at
      ? dayjsUTC(sensor.last_sample_collected_at)
      : null
  )
  const [lastSampleReported, setLastSampleReported] = useState<Dayjs | null>(
    sensor?.last_sample_result_reported_at
      ? dayjsUTC(sensor.last_sample_result_reported_at)
      : null
  )
  const [nodeId, setNodeId] = useState(
    sensor?.platform_id ?? initialPlatformId ?? ""
  )
  const [contactIds, setContactIds] = useState<string[]>(
    () =>
      sensor?.contacts?.flatMap(c => (c.contact_id ? [c.contact_id] : [])) ?? []
  )
  const [sensorNotes, setSensorNotes] = useState(sensor?.sensor_notes ?? "")

  const [contactDialogOpen, setContactDialogOpen] = useState(false)
  const [nodeDialogOpen, setNodeDialogOpen] = useState(false)
  const [formError, setFormError] = useState("")

  const { data: contacts } = useListContactsBiomeshContactListGet()
  const { data: nodes } = useListPlatformsBiomeshPlatformListGet()

  const { mutateAsync: createSensor, isPending: createPending } =
    useCreateSensorBiomeshSensorPost()
  const { mutateAsync: updateSensor, isPending: updatePending } =
    useUpdateSensorBiomeshSensorSensorIdPut()

  const isPending = createPending || updatePending

  const handleSave = useCallback(async () => {
    setFormError("")

    if (!sensorName.trim()) {
      setFormError("Sensor name is required")
      return
    }
    if (!operationalStatus) {
      setFormError("Operational status is required")
      return
    }
    if (!isOperationalStatus(operationalStatus)) {
      setFormError("Invalid operational status")
      return
    }
    if (!isSensorType(sensorType)) {
      setFormError("Invalid sensor type")
      return
    }
    const validatedFunction =
      sensorFunction && isSensorFunction(sensorFunction) ? sensorFunction : null
    const payload = {
      sensor_name: sensorName.trim(),
      operational_status: operationalStatus,
      sensor_type: sensorType,
      sensor_function: validatedFunction,
      sensor_make_model: sensorMakeModel.trim() || null,
      sensor_notes: sensorNotes.trim() || null,
      last_sample_collected_at: lastSampleCollected
        ? lastSampleCollected.toISOString()
        : null,
      last_sample_result_reported_at: lastSampleReported
        ? lastSampleReported.toISOString()
        : null,
      contact_ids: contactIds,
      platform_id: nodeId || null,
    }

    try {
      if (sensor) {
        await updateSensor({ sensorId: sensor.sensor_id, data: payload })
        enqueueSnackbar("Sensor updated", { variant: "success" })
        onSaved?.(sensor.sensor_id)
      } else {
        const result = await createSensor({ data: payload })
        enqueueSnackbar("Sensor created", { variant: "success" })
        onSaved?.(result.sensor_id)
      }
      await queryClient.invalidateQueries({
        queryKey: getListSensorsBiomeshSensorListGetQueryKey(),
      })
      onClose()
    } catch (err) {
      setFormError(extractApiError(err, "Failed to save sensor"))
    }
  }, [
    sensorName,
    operationalStatus,
    sensorType,
    sensorFunction,
    sensorMakeModel,
    sensorNotes,
    lastSampleCollected,
    lastSampleReported,
    contactIds,
    nodeId,
    sensor,
    createSensor,
    updateSensor,
    queryClient,
    onClose,
    onSaved,
  ])

  interface ContactOption {
    id: string
    label: string
  }

  const contactOptions: ContactOption[] = (
    contacts?.map(c => ({
      id: c.contact_id,
      label: `${c.rank ? `${c.rank} ` : ""}${c.first_name} ${c.last_name}`,
    })) ?? []
  ).sort((a, b) => a.label.localeCompare(b.label))

  const handleNodeSelect = (value: string) => {
    if (value === CREATE_NEW_SENTINEL) {
      setNodeDialogOpen(true)
    } else {
      setNodeId(value)
    }
  }

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Modal
          open={open}
          onClose={onClose}
          onConfirm={() => void handleSave()}
          title={isEdit ? "Edit Sensor" : "New Sensor"}
          confirmLabel="Save"
          disableBtns={isPending}
        >
          <Stack spacing={1.5}>
            <TextField
              label="Sensor Name"
              required
              fullWidth
              value={sensorName}
              onChange={e => setSensorName(e.target.value)}
            />

            <TextField
              label="Operational Status"
              select
              required
              fullWidth
              value={operationalStatus}
              onChange={e => setOperationalStatus(e.target.value)}
              helperText="Status of the sensor's ability to correctly perform its function."
            >
              {operationalStatusOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Sensor Type"
              select
              fullWidth
              value={sensorType}
              onChange={e => setSensorType(e.target.value)}
            >
              {sensorTypeOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Sensor Function"
              select
              fullWidth
              value={sensorFunction}
              onChange={e => setSensorFunction(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {sensorFunctionOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Sensor Make / Model"
              fullWidth
              value={sensorMakeModel}
              onChange={e => setSensorMakeModel(e.target.value)}
            />

            <DatePicker
              label="Date of Last Sample Collection"
              value={lastSampleCollected}
              onChange={setLastSampleCollected}
              slotProps={{ textField: { fullWidth: true } }}
            />

            <DatePicker
              label="Date of Last Sample Result Report"
              value={lastSampleReported}
              onChange={setLastSampleReported}
              slotProps={{ textField: { fullWidth: true } }}
            />

            <TextField
              label="Node Association"
              select
              fullWidth
              value={nodeId}
              onChange={e => handleNodeSelect(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {nodes?.map(p => (
                <MenuItem key={p.platform_id} value={p.platform_id}>
                  {p.name}
                </MenuItem>
              ))}
              <MenuItem value={CREATE_NEW_SENTINEL}>
                <Typography color="primary">+ Create New</Typography>
              </MenuItem>
            </TextField>

            <MultiAutocomplete
              label="Contacts"
              options={contactOptions}
              selectedIds={contactIds}
              onChange={setContactIds}
              onCreateNew={() => setContactDialogOpen(true)}
            />

            <TextField
              label="Sensor Notes"
              fullWidth
              multiline
              minRows={2}
              value={sensorNotes}
              onChange={e => setSensorNotes(e.target.value)}
            />
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </Modal>
      </LocalizationProvider>

      <ContactFormDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        onSaved={id => setContactIds(prev => [...prev, id])}
      />

      <NodeFormDialog
        open={nodeDialogOpen}
        onClose={() => setNodeDialogOpen(false)}
        onSaved={(id: string) => setNodeId(id)}
      />
    </>
  )
}
